import React from 'react';
import { Box } from '@mui/material';
import { NEUTRAL_BANNER_BASE } from './headerLayout';

/**
 * AppBar の裏に敷く帯。**SP 専用。**
 *
 * SP ではバナーを AppBar の下へ潜り込ませない（200px しかない絵の上端 56px が
 * ロゴ・検索・アイコンと重なって読めなくなるため）。ただし潜り込ませないと
 * 画面最上部が素の暗色になり、没入感が完全に消える。
 *
 * そこで**バナーと同じ絵を強くぼかした帯**を AppBar の裏に敷く。
 * 色と雰囲気は画面最上部まで届くのに、隠れる情報はゼロになる
 * （ぼけた帯は読む対象ではないので、実物のバナーが下に全部見えていれば損失にならない）。
 *
 * 【画像が無い人にもそのまま効く】
 * 渡ってくる background は bannerBaseSx の出力で、画像が無ければアクセント色由来の
 * グラデーションになっている。**分岐は要らない。** グラデーションをぼかしても壊れないので、
 * 画像がある人は「絵のにじみ」、無い人は「テーマ色のガラス」になる。
 *
 * 【コスト】
 * blur はペイント時のフィルタだが、この帯は静止なのでラスタライズは初回1回きり。
 * **バナーのエフェクト（ズーム／スイープ）には追従させないこと。**
 * 追従させると毎フレーム blur を掛け直すことになる。強くぼかしてあるので同期ズレは見えない。
 */
const BannerTopWash: React.FC<{
  /** bannerBaseSx の出力をそのまま渡す（画像 or アクセント由来グラデーション） */
  background: string;
  /** AppBar の高さ。セーフエリアはこの中で足す */
  height: number;
  /**
   * 絵を出してよいか。**バナー本体の出し方と必ず揃えること。**
   * false のあいだは無彩色の下地だけを見せ、true でフェードインする。
   * 読み込み中も帯自体は出す（AppBar が透過なので、消すと上端が素の暗色になる）が、
   * その時点では「誰の色でもない」状態にしておく。ここだけ即時に色を出すと、
   * 本体がフェードしている最中に画面最上部だけ先に色が変わる。
   */
  revealed?: boolean;
}> = ({ background, height, revealed = true }) => (
  <Box
    aria-hidden
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      // ノッチ／PWA のセーフエリア分まで伸ばす。ここが抜けると上端に素の色が出る
      height: `calc(${height}px + env(safe-area-inset-top, 0px))`,
      // AppBar(1100) より下、ページ内容より上。AppBar のボタンを奪わないよう当たり判定も切る
      zIndex: 1050,
      pointerEvents: 'none',
      // blur は要素の外を参照できず端が透けるので、内側を拡大してから切り落とす
      overflow: 'hidden',
      // フェードインの下地。revealed=false のあいだ見えているのはこれ
      background: NEUTRAL_BANNER_BASE,
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        // 拡大とぼかしで端がやせるぶん、四方に広く取る
        inset: '-40%',
        background,
        // 形が読めるとバナーの複製だと分かって「二重に見えている」感じが出る。
        // 形が完全に溶ける強さまでぼかすこと。彩度は上げて色だけ残す
        filter: 'blur(28px) saturate(1.35) brightness(0.55)',
        transform: 'scale(1.2)',
        opacity: revealed ? 1 : 0,
        transition: 'opacity 0.4s ease',
      }}
    />
    {/* 文字を読ませる層。下へ向かって薄くして、直下のバナーとの段差を目立たせない */}
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, rgba(18,18,18,0.62) 0%, rgba(18,18,18,0.42) 100%)',
      }}
    />
  </Box>
);

export default BannerTopWash;
